import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Seo } from '../components/Seo';
import { LocalizedLink } from '../components/LocalizedLink';
import { getProduct, getCategory } from '../data/products';

export function ProductDetail() {
  const { t } = useTranslation();
  const { category: categorySlug = '', productId = '' } = useParams();
  const product = getProduct(productId);
  const category = getCategory(categorySlug);

  if (!product) {
    return (
      <section className="section">
        <h1 className="text-3xl font-black">{t('products:detail.notFound')}</h1>
        <LocalizedLink className="btn-primary mt-6" to="/products">
          {t('products:detail.backToProducts')}
        </LocalizedLink>
      </section>
    );
  }

  const specs = Object.entries(product.specs || {});
  const localizedCategory = category ? t(`products:categories.${category.slug}.title`) : product.category;
  const localizedDescription = t(`products:descriptions.${product.id}`, { defaultValue: product.description });

  return (
    <>
      <Seo
        title={`${product.name} | GyroNavi`}
        description={product.description}
        path={`/products/${product.category}/${product.id}`}
      />
      <section className="section">
        <nav className="mb-4 text-sm text-slate-500">
          <LocalizedLink to="/" className="hover:text-aqua">{t('products:breadcrumb.home')}</LocalizedLink> ›{' '}
          <LocalizedLink to="/products" className="hover:text-aqua">{t('products:breadcrumb.products')}</LocalizedLink> ›{' '}
          <LocalizedLink to={`/products/${product.category}`} className="hover:text-aqua">
            {localizedCategory}
          </LocalizedLink>{' '}
          › <span className="text-ink">{product.name}</span>
        </nav>

        <div className="grid gap-10 lg:grid-cols-[1fr_1fr]">
          <div className="flex items-center justify-center rounded-lg border border-slate-200 bg-white p-10 shadow-sm">
            <img src={product.image} alt={product.name} className="max-h-96 object-contain" />
          </div>

          <div>
            <p className="eyebrow">{localizedCategory}</p>
            <h1 className="mt-2 text-3xl font-black leading-tight text-ink">{product.name}</h1>
            <p className="mt-4 leading-7 text-slate-600">{localizedDescription}</p>

            <div className="mt-6 flex flex-wrap gap-2">
              {product.accuracy ? <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{product.accuracy}</span> : null}
              {product.axis ? <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{product.axis}-Axis</span> : null}
              {product.interface ? <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{product.interface}</span> : null}
              {product.temp ? <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{product.temp}°C</span> : null}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <LocalizedLink to="/contact" className="btn-primary px-5 py-2.5">
                {t('products:detail.getQuote')}
              </LocalizedLink>
              <LocalizedLink to={`/products/${product.category}`} className="btn-secondary px-5 py-2.5">
                {t('products:detail.backToCategory')}
              </LocalizedLink>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-black text-ink">{t('products:detail.specs')}</h2>
          <div className="mt-5 overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <tbody>
                {specs.map(([key, value], index) => (
                  <tr key={key} className={index % 2 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="border-t border-slate-100 px-5 py-3 font-semibold text-slate-600">{key}</td>
                    <td className="border-t border-slate-100 px-5 py-3 text-slate-700">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
